/**
 * TopicHubGrid — public landing-page hero grid linking to every SEO
 * topic hub (`/topics/:slug`). Rendered on `/` so cold visitors from
 * Google Search discover the hubs without needing the URL.
 *
 * Data source: same `/api/topics` endpoint as the nav dropdown.
 * Silently renders nothing when the API returns no topics (keeps the
 * landing page shape stable during a fresh deploy).
 */
import React, { useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { AuthContext } from '../App';
import { Compass, ArrowRight } from 'lucide-react';

const TopicHubGrid = () => {
  const { API } = useContext(AuthContext);
  const navigate = useNavigate();
  const [topics, setTopics] = useState([]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/topics`);
        if (!cancelled && Array.isArray(data)) setTopics(data);
      } catch { /* soft fail — grid just hides */ }
    })();
    return () => { cancelled = true; };
  }, [API]);

  if (!topics.length) return null;

  return (
    <section
      id="topics"
      className="container mx-auto px-6 py-20"
      data-testid="landing-topic-hubs"
    >
      <div className="max-w-2xl mb-10">
        <span className="eyebrow inline-flex items-center gap-1.5">
          <Compass className="w-3.5 h-3.5" /> Explore by topic
        </span>
        <h2 className="font-heading text-4xl sm:text-5xl font-extrabold tracking-tight mt-3">
          Every guide, course & article,
          <br />
          <span className="text-amber-brand">grouped by what you're actually doing.</span>
        </h2>
        <p className="mt-4 text-muted-foreground text-lg">
          Meals, produce, dairy, business, emergency prep — pick a lane and
          jump straight to the courses and articles that match.
        </p>
      </div>
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
        {topics.map((t, i) => (
          <button
            key={t.slug}
            type="button"
            onClick={() => navigate(`/topics/${t.slug}`)}
            className="pro-card-interactive text-left p-6 group"
            style={{ '--i': i }}
            data-testid={`topic-hub-card-${t.slug}`}
          >
            <div className="flex items-start justify-between">
              <div className="h-11 w-11 rounded-xl bg-primary/10 text-primary flex items-center justify-center group-hover:bg-primary group-hover:text-primary-foreground transition-colors">
                <Compass className="w-5 h-5" strokeWidth={2} />
              </div>
              <ArrowRight className="w-4 h-4 text-muted-foreground opacity-0 group-hover:opacity-100 group-hover:translate-x-1 transition" />
            </div>
            <h3 className="mt-4 font-heading text-lg font-bold tracking-tight">
              {t.title}
            </h3>
            {t.description && (
              <p className="mt-2 text-muted-foreground leading-relaxed text-sm line-clamp-3">
                {t.description}
              </p>
            )}
            <div className="mt-3 text-[11px] font-extrabold text-primary uppercase tracking-widest">
              Explore topic →
            </div>
          </button>
        ))}
      </div>
    </section>
  );
};

export default TopicHubGrid;
